import { find } from 'lodash';
import moment from 'moment';

import { shipmentStatuses } from 'components/modules/shipments/mappings';
import { isIntermodalShipmentType } from './shipment-types';
import {
  SHIPMENT_STATUS_MAPPING,
  SHORTHAUL_SHIPMENT_GROUP,
} from 'constants/ShipmentTypes';

const acceptedStatus = find(shipmentStatuses, { slug: 'accepted' });

export function getShipmentTimezone(shipment) {
  if (!shipment?.stops?.length) {
    return DEFAULT_TIMEZONE;
  }

  for (const stop of shipment.stops) {
    for (const loc of stop.location || []) {
      if (loc?.location_timezone) return loc.location_timezone;
      if (loc?.market?.timezone) return loc.market.timezone;
    }
  }

  return DEFAULT_TIMEZONE;
}

/**
 * isShipmentActive returns true if the shipment has a slug with an associated
 * code greater than or equal to the same for slug='acceptance'
 *
 */
export function isShipmentActive(shipment) {
  const { status } = shipment;
  const currentStatus = find(shipmentStatuses, { slug: status });
  return (
    currentStatus &&
    acceptedStatus &&
    currentStatus.code >= acceptedStatus.code &&
    shipment.current
  );
}

export function isAssignedCarrier(shipment) {
  return !!shipment.carrier;
}

export function isDrayageShipmentNotCanceled(shipment) {
  return !shipment.canceled && isIntermodalShipmentType(shipment);
}

export function isShipmentClosed(shipment) {
  return (
    !shipment.current &&
    shipment.timeline &&
    shipment.timeline.job_closed &&
    shipment.timeline.job_closed.at
  );
}

export function isShipmentCanceled(shipment) {
  return (
    !shipment.current &&
    (shipment.status === 'job_canceled' || shipment.canceled)
  );
}

export function isShipmentAccepted(shipment) {
  return shipment.accepted;
}

export function isActiveShipment(shipment) {
  return shipment.current && !shipment.canceled;
}

export function canEditAppointmentWindow(shipment, isAdmin = false) {
  if (isAdmin) {
    return true;
  }

  return !isShipmentClosed(shipment) && !isShipmentCanceled(shipment);
}

export function getDeliveryOrderRecipients(carrier) {
  let recipient = '';
  const emails = [];
  if (carrier && carrier.notification_preferences) {
    const contacts = carrier.notification_preferences.filter(
      (contact) => contact.type === 'drayage_delivery_order_contacts',
    );
    if (contacts.length) {
      const { email } = contacts[0];
      if (email && email.recipients && email.recipients.length) {
        for (let i = 0; i < email.recipients.length; i += 1) {
          recipient = email.recipients[i].trim();
          emails.push(recipient);
        }
        return emails.join(',');
      }
    }
  }
  return recipient;
}

export function getDeliveryOrderRecipient(carrier) {
  let recipient = '';
  if (carrier && carrier.notification_preferences) {
    const contacts = carrier.notification_preferences.filter(
      (contact) => contact.type === 'drayage_delivery_order_contacts',
    );
    if (contacts.length) {
      const { email } = contacts[0];
      if (email && email.recipients && email.recipients.length) {
        recipient = email.recipients[0].trim();
      }
    }
  }
  return recipient;
}

export function getRateCardEmailRecipient(account) {
  let recipient = '';
  if (account && account.notification_preferences) {
    const contacts = account.notification_preferences.filter(
      (contact) => contact.type === 'drayage_rate_card',
    );
    if (contacts.length) {
      const { email } = contacts[0];
      if (
        email &&
        !email.silent &&
        email.recipients &&
        email.recipients.length
      ) {
        recipient = email.recipients.map((r) => r.trim()).join(', ');
      } else {
        recipient = account.email;
      }
    }
  }
  return recipient;
}

export function getShipmentEquipment(shipment) {
  return shipment.stops.reduce((equipments, stop) => {
    const equipmentIds = equipments.map((eq) => eq._id);
    const stopEquipments = stop.equipments.filter(
      (eq) => !equipmentIds.includes(eq._id),
    );

    return equipments.concat(...stopEquipments);
  }, []);
}

export const calculatePalletSpaces = (loads) => {
  return loads.reduce((totalSpaces, load) => {
    totalSpaces = load.spaces ? (totalSpaces += load.spaces) : totalSpaces;
    return totalSpaces;
  }, 0);
};

export const isOutsideWindow = (timestamp, stop) => {
  if (moment(timestamp).isBefore(stop.window_start)) return true;
  if (moment(timestamp).isAfter(stop.window_end)) return true;
  return false;
};

export const requiresReasonCapture = (shipper, stop, timestamp = moment()) => {
  if (
    (shipper.requires_late_early_reason_code &&
      isOutsideWindow(timestamp, stop)) ||
    (shipper.requires_on_time_reason_code && !isOutsideWindow(timestamp, stop))
  ) {
    return true;
  }
  return false;
};

export const getShipmentPreferenceType = (shipmentType) => {
  let preferenceType = null;
  if (shipmentType === 'drayage' || shipmentType === 'rail') {
    preferenceType = 'drayage';
  } else if (
    shipmentType === 'shorthaul' ||
    shipmentType === 'longhaul' ||
    shipmentType === 'dayrate'
  ) {
    preferenceType = 'shorthaul';
  }
  return preferenceType;
};

export const checkShorthaulAppointmentNeeded = (
  shipment_type = '',
  shipment_status = '',
) => {
  return (
    SHORTHAUL_SHIPMENT_GROUP.includes(shipment_type) &&
    shipment_status === SHIPMENT_STATUS_MAPPING.NEEDS_APPT
  );
};
